<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  featurePrefix: {
    type: String,
    default: 'CAPTAIN',
  },
  features: {
    type: Array,
    default: () => [
      { key: 'ASSISTANTS', icon: 'i-lucide-bot' },
      { key: 'COPILOT', icon: 'i-lucide-sparkles' },
      { key: 'KNOWLEDGE', icon: 'i-lucide-book-open' },
      { key: 'CREDITS', icon: 'i-lucide-coins' },
    ],
  },
});

const { t } = useI18n();
const router = useRouter();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();
const currentUser = useMapGetter('getCurrentUser');

const isSuperAdmin = computed(() => currentUser.value.type === 'SuperAdmin');

const openBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <section
    class="flex flex-col items-center justify-center w-full px-6 py-16 text-center"
  >
    <span
      class="grid mb-5 rounded-2xl size-14 place-content-center bg-n-violet-3"
    >
      <Icon icon="i-lucide-sparkles" class="text-n-brand size-7" />
    </span>
    <h1 class="mb-2 text-2xl font-semibold tracking-tight text-n-slate-12">
      {{ t(`${featurePrefix}.PAYWALL.TITLE`) }}
    </h1>
    <p class="max-w-lg mb-8 text-body-main text-n-slate-11">
      {{ t(`${featurePrefix}.PAYWALL.DESCRIPTION`) }}
    </p>
    <div
      v-if="features.length"
      class="grid w-full max-w-2xl grid-cols-1 gap-3 mb-8 sm:grid-cols-2"
    >
      <div
        v-for="feature in features"
        :key="feature.key"
        class="flex items-start gap-3 p-4 text-left border rounded-xl border-n-weak bg-n-solid-1"
      >
        <span
          class="grid mt-0.5 rounded-lg shrink-0 size-8 place-content-center bg-n-violet-3"
        >
          <Icon :icon="feature.icon" class="size-4 text-n-brand" />
        </span>
        <div>
          <p class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t(`${featurePrefix}.PAYWALL.FEATURES.${feature.key}.TITLE`) }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            {{
              t(`${featurePrefix}.PAYWALL.FEATURES.${feature.key}.DESCRIPTION`)
            }}
          </p>
        </div>
      </div>
    </div>
    <a v-if="isSuperAdmin" href="/super_admin" class="inline-block">
      <Button
        :label="t(`${featurePrefix}.PAYWALL.UPGRADE_NOW`)"
        size="lg"
        class="w-56"
      />
    </a>
    <Button
      v-else-if="isAdmin"
      :label="t(`${featurePrefix}.PAYWALL.UPGRADE_NOW`)"
      size="lg"
      class="w-56"
      @click="openBilling"
    />
    <p v-else class="mb-0 text-sm text-n-slate-11">
      {{ t(`${featurePrefix}.PAYWALL.ASK_ADMIN`) }}
    </p>
    <span v-if="isSuperAdmin || isAdmin" class="mt-3 text-xs text-n-slate-11">
      {{ t(`${featurePrefix}.PAYWALL.CANCEL_ANYTIME`) }}
    </span>
  </section>
</template>
